import {useCallback} from 'react';

import {useApiWithNotifications} from 'src/hooks/api-with-notification/useApiWithNotifications';
import {approveRequest, declineRequest} from 'src/openapi/admin/apis/ApprovalRequestApi';

import type {InlineObject} from 'src/openapi/admin/models/InlineObject';

const NOTIFICATION_MESSAGES = {
    approve: {
        success: 'Access granted to user. To undo, contact your PitchBook representative.',
        error: 'Cannot approve the request. Please, contact your PitchBook representative.',
        exception: 'An unexpected error occurred while approving the request',
    },
    decline: {
        success: 'Access denied to user. To undo, contact your PitchBook representative.',
        error: 'Cannot decline the request. Please, contact your PitchBook representative.',
        exception: 'An unexpected error occurred while declining the request',
    },
} as const;

type ActionType = 'approve' | 'decline';

export const useApprovalRequestActions = () => {
    const {executeWithNotifications} = useApiWithNotifications();

    const handleAction = useCallback(
        async (
            action: ActionType,
            requestId: string,
            body?: InlineObject,
        ): Promise<boolean> => {
            const apiAction =
                action === 'approve'
                    ? approveRequest({requestId})
                    : declineRequest({requestId, inlineObject: body!});

            const result = await executeWithNotifications(
                apiAction,
                NOTIFICATION_MESSAGES[action],
                action,
            );

            return result.success;
        },
        [executeWithNotifications],
    );

    const approve = useCallback(
        (requestId: string) => handleAction('approve', requestId),
        [handleAction],
    );

    const decline = useCallback(
        (requestId: string, body: InlineObject) => handleAction('decline', requestId, body),
        [handleAction],
    );

    return {approve, decline};
};
